import { useState, useEffect, useRef } from "react";
import { Search, Plus, X, CalendarDays, Trash } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Skeleton } from "@/components/ui/skeleton";
import { fmtDate, dateIST } from "@emr/shared/india";
import { FORMULATIONS, isValidFrequency, normalizeFrequency, type TaperStep } from "@emr/shared";
import { useDrugs } from "@/lib/queries";
import { useDebouncedValue } from "@/hooks/useDebouncedValue";
import { FrequencyInput } from "./FrequencyInput";
import { useDropdownNav } from "@/hooks/useDropdownNav";
import type { MedicineRow } from "@/lib/queries";
import {
  type DraftMedicine, type DraftTaperRow, TAPER_GRID, TAPER_GRID_DRUG,
  addDays, stepToDate, rechainTaperRows, newTaperRow,
  draftToTaperSteps, medicineToDraft,
} from "./taperDraft";
import { TaperRowDrugSearch } from "./TaperRowDrugSearch";

interface Props {
  open: boolean;
  onOpenChange: (v: boolean) => void;
  medicine: MedicineRow | null;
  onSave: (m: Partial<MedicineRow>) => void;
  onDelete: () => void;
}

// ---- Draft state ----
// DraftTaperRow / DraftMedicine, the grid templates, the date + rechain
// helpers, newTaperRow, the TaperStep conversion and TaperRowDrugSearch now
// live in ./taperDraft (shared with PrescriptionTable + the templates page,
// Batch 7 Wave 3). This dialog keeps its per-keystroke drug query in the
// per-row search (debounceMs={0}) — the behaviour it always had.

export function EditMedicineDialog({ open, onOpenChange, medicine, onSave, onDelete }: Props) {
  // `draft` is the editable form state (same shape as the add-drug draft in
  // PrescriptionTable). An existing taper protocol loads straight into the
  // editable grid (same format as entry) — from-dates, frequencies, days,
  // per-step instructions and per-step drugs are all editable in place.
  const [draft, setDraft] = useState<DraftMedicine | null>(null);
  // Drug-name search (top of the dialog).
  const [nameQuery, setNameQuery] = useState("");
  const [showNameDropdown, setShowNameDropdown] = useState(false);
  const nameSearchRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (open && medicine) {
      // Pre-fill the draft from the stored row; a stored taper converts back
      // to the draft grid shape via medicineToDraft (days: number → string).
      setDraft(medicineToDraft(medicine));
      setNameQuery(medicine.name);
      setShowNameDropdown(false);
    }
  }, [open, medicine]);

  const set = <K extends keyof DraftMedicine>(k: K, v: DraftMedicine[K]) =>
    setDraft((prev) => (prev ? { ...prev, [k]: v } : prev));

  // ---- Drug-name search (live /drugs?q=…) ----
  // Debounced (250ms): one request per typing pause, not per keystroke.
  const debouncedNameQuery = useDebouncedValue(nameQuery, 250);
  const { data: drugsData, isLoading: drugsLoading } = useDrugs(debouncedNameQuery.length >= 3 ? debouncedNameQuery : "");
  const drugs = (drugsData && "rows" in drugsData ? drugsData.rows : Array.isArray(drugsData) ? drugsData : []) as { id: string; name: string; composition: string | null; formulation: string | null; strength: string | null }[];

  // Dropdown items: up to 10 drug matches + 1 "Keep as entered" entry.
  const editDrugResults = drugs.slice(0, 10);
  const editDrugListCount = editDrugResults.length + 1;

  const { highlight: editDrugHighlight, highlightRef: editDrugHighlightRef, onKeyDown: onEditDrugKeyDown } = useDropdownNav({
    count: showNameDropdown && nameQuery.length >= 3 && !drugsLoading ? editDrugListCount : 0,
    onSelect: (i) => {
      if (i < editDrugResults.length) selectDrug(editDrugResults[i]);
      else { setShowNameDropdown(false); }
    },
    inputRef: nameSearchRef,
    onEscape: () => setShowNameDropdown(false),
  });

  const selectDrug = (d: { id: string; name: string; composition: string | null; formulation: string | null; strength: string | null }) => {
    setDraft((prev) => prev ? {
      ...prev,
      drugId: d.id,
      name: d.name,
      composition: d.composition,
      formulation: (d.formulation as MedicineRow["formulation"]) ?? prev.formulation ?? "Tablet",
      strength: d.strength ?? prev.strength,
    } : prev);
    setNameQuery(d.name);
    setShowNameDropdown(false);
  };

  // ---- Taper grid helpers (same logic as PrescriptionTable; the row builder
  // + date/rechain helpers now live in ./taperDraft) ----
  const enableTaper = () => {
    if (!draft) return;
    // An existing taper is loaded into the draft; enabling when one exists
    // must not wipe it (the checkbox is checked, not re-seeded).
    if (draft.taper) return;
    set("taper", [newTaperRow(dateIST(), draft, draft.perStrength)]);
  };
  const disableTaper = () => set("taper", null);

  const togglePerStrength = (checked: boolean) => {
    if (!draft) return;
    if (!checked) {
      setDraft({
        ...draft,
        perStrength: false,
        taper: (draft.taper ?? []).map((r) => ({ ...r, name: "", strength: "", composition: null, formulation: null, drugId: null })),
      });
    } else {
      setDraft({
        ...draft,
        perStrength: true,
        taper: (draft.taper ?? []).map((r) => ({ ...r, name: r.name || draft.name, strength: r.strength || draft.strength, composition: r.composition ?? draft.composition, formulation: r.formulation ?? draft.formulation, drugId: r.drugId ?? draft.drugId })),
      });
    }
  };
  // Edit one taper row. A from-date or days change re-chains every FOLLOWING
  // row's from-date (prev To + 1) so start/end stay continuous — the same
  // rule the "+" append uses (user request 2026-10-30: changing days must
  // recalculate start/end days of the following periods).
  const setTaperRow = (key: string, patch: Partial<DraftTaperRow>) => {
    if (!draft?.taper) return;
    const idx = draft.taper.findIndex((r) => r.key === key);
    if (idx < 0) return;
    const patched = draft.taper.map((r) => (r.key === key ? { ...r, ...patch } : r));
    set("taper", "fromDate" in patch || "days" in patch ? rechainTaperRows(patched, idx) : patched);
  };
  const removeTaperRow = (key: string) => {
    if (!draft?.taper) return;
    set("taper", draft.taper.filter((r) => r.key !== key));
  };
  const addTaperRow = () => {
    if (!draft?.taper) return;
    const last = draft.taper[draft.taper.length - 1];
    let fromDate = dateIST();
    if (last) {
      const to = stepToDate(last.fromDate, last.days);
      if (to) fromDate = addDays(to, 1);
    }
    const inherit = draft.perStrength && last
      ? { name: last.name || draft.name, strength: last.strength || draft.strength, composition: last.composition ?? draft.composition, formulation: last.formulation ?? draft.formulation, drugId: last.drugId ?? draft.drugId }
      : draft;
    set("taper", [...draft.taper, newTaperRow(fromDate, inherit as DraftMedicine, draft.perStrength)]);
  };

  // ---- Save (same validation as confirmAdd) ----
  const save = () => {
    if (!draft || !medicine) return;
    if (!draft.name.trim()) { alert("Enter a drug name."); return; }
    const freq = normalizeFrequency(draft.frequency);
    if (draft.taper) {
      for (const [i, r] of draft.taper.entries()) {
        if (!r.fromDate) { alert(`Protocol row ${i + 1}: pick a From date`); return; }
        if (!r.days || parseInt(r.days, 10) < 1) { alert(`Protocol row ${i + 1}: enter the number of days`); return; }
        if (!normalizeFrequency(r.frequency) || !isValidFrequency(normalizeFrequency(r.frequency))) {
          alert(`Protocol row ${i + 1}: enter a frequency like 1-1-1-1`);
          return;
        }
        if (draft.perStrength && !r.name.trim()) {
          alert(`Protocol row ${i + 1}: pick a drug for this row`);
          return;
        }
      }
      if (draft.taper.length === 0) { alert("Add at least one protocol row"); return; }
    } else {
      if (!freq && !draft.instructions.trim()) {
        alert("Enter a frequency or instructions.");
        return;
      }
      if (!isValidFrequency(freq)) {
        alert("Frequency looks invalid — use numbers like 1-1-1 or 1.5-0-1.");
        return;
      }
    }
    const taperSteps: TaperStep[] | null = draftToTaperSteps(draft);
    onSave({
      drugId: draft.drugId,
      name: draft.name,
      composition: draft.composition,
      formulation: draft.formulation,
      strength: draft.strength || null,
      frequency: taperSteps ? null : (freq || null),
      taper: taperSteps,
      duration: taperSteps ? null : (draft.days ? `${draft.days} days` : null),
      instructions: draft.instructions || null,
    });
    onOpenChange(false);
  };

  const del = () => {
    onDelete();
    onOpenChange(false);
  };

  const cancel = () => onOpenChange(false);

  if (!draft) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-5xl">
        <DialogHeader><DialogTitle>Edit medicine</DialogTitle></DialogHeader>

        {/* Drug-name search — full-width row (dropdown needs vertical space). */}
        <div className="relative">
          <Label className="text-xs">Drug name</Label>
          <div className="relative">
            <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted" />
            <Input
              ref={nameSearchRef}
              className="pl-9"
              value={nameQuery}
              onChange={(e) => {
                const v = e.target.value;
                setNameQuery(v);
                set("name", v);
                setShowNameDropdown(v.length >= 3);
              }}
              onFocus={() => nameQuery.length >= 3 && setShowNameDropdown(true)}
              onBlur={() => setTimeout(() => setShowNameDropdown(false), 150)}
              onKeyDown={(e) => {
                if (showNameDropdown && nameQuery.length >= 3 && !drugsLoading && editDrugListCount > 0) {
                  onEditDrugKeyDown(e);
                } else if (e.key === "Enter") {
                  e.preventDefault();
                  save();
                }
              }}
              placeholder="Search drug name or composition (3+ chars)…"
            />
            {showNameDropdown && nameQuery.length >= 3 && (
              <div className="visible-scrollbar absolute z-50 mt-1 max-h-60 w-full overscroll-contain overflow-y-auto rounded-md border border-cream-200 bg-cream-100 shadow-lg dark:border-dark-700 dark:bg-dark-800">
                {drugsLoading ? (
                  <div className="p-2"><Skeleton className="h-6" /></div>
                ) : drugs.length === 0 ? (
                  <button
                    type="button"
                    ref={editDrugHighlight === 0 ? editDrugHighlightRef : undefined}
                    className={`flex w-full items-center gap-2 px-3 py-2 text-left text-sm hover:bg-cream-200 dark:hover:bg-dark-700 ${
                      editDrugHighlight === 0 ? "bg-cream-200 dark:bg-dark-700" : ""
                    }`}
                    onMouseDown={(e) => { e.preventDefault(); setShowNameDropdown(false); }}
                  >
                    <Search size={14} className="text-muted" /> No match — keep "{nameQuery}" as entered
                  </button>
                ) : (
                  <>
                    {editDrugResults.map((d, idx) => (
                      <button
                        key={d.id}
                        type="button"
                        ref={idx === editDrugHighlight ? editDrugHighlightRef : undefined}
                        className={`flex w-full flex-col px-3 py-2 text-left text-sm hover:bg-cream-200 dark:hover:bg-dark-700 ${
                          idx === editDrugHighlight ? "bg-cream-200 dark:bg-dark-700" : ""
                        }`}
                        onMouseDown={(e) => { e.preventDefault(); selectDrug(d); }}
                      >
                        <span className="font-medium text-ink dark:text-cream-50">{d.name}</span>
                        <span className="text-xs text-muted">
                          {[d.composition, d.formulation, d.strength].filter(Boolean).join(" · ")}
                        </span>
                      </button>
                    ))}
                    <button
                      type="button"
                      ref={editDrugResults.length === editDrugHighlight ? editDrugHighlightRef : undefined}
                      className={`flex w-full items-center gap-2 border-t border-cream-200 px-3 py-2 text-left text-sm text-maroon-600 hover:bg-cream-200 dark:border-dark-700 dark:hover:bg-dark-700 ${
                        editDrugResults.length === editDrugHighlight ? "bg-cream-200 dark:bg-dark-700" : ""
                      }`}
                      onMouseDown={(e) => { e.preventDefault(); setShowNameDropdown(false); }}
                    >
                      Keep "{nameQuery}" as entered
                    </button>
                  </>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Composition / Formulation / Strength — one row, matching the
            entry dialog's compact field layout. */}
        <div className="grid grid-cols-[1fr_140px_120px] items-end gap-2">
          <div>
            <Label className="text-xs">Composition</Label>
            <Input value={draft.composition ?? ""} onChange={(e) => set("composition", e.target.value)} />
          </div>
          <div>
            <Label className="text-xs">Formulation</Label>
            <select
              className="h-9 w-full rounded-md border border-input bg-transparent px-3 text-sm"
              value={draft.formulation ?? "Tablet"}
              onChange={(e) => set("formulation", e.target.value as MedicineRow["formulation"])}
            >
              {FORMULATIONS.map((f) => <option key={f} value={f}>{f}</option>)}
            </select>
          </div>
          <div>
            <Label className="text-xs">Strength / dosage</Label>
            <Input value={draft.strength} onChange={(e) => set("strength", e.target.value)} placeholder="e.g. 500mg" />
          </div>
        </div>

        {/* Escalation / De-escalation protocol checkboxes — same as the
            add-drug draft panel. An existing taper loads straight into the
            editable grid below (same format as entry). */}
        <div className="flex items-center gap-2">
          <Checkbox
            id="edit-taper-toggle"
            checked={!!draft.taper}
            onCheckedChange={(checked) => (checked ? enableTaper() : disableTaper())}
          />
          <Label htmlFor="edit-taper-toggle" className="text-xs cursor-pointer">
            Escalation / De-escalation protocol
          </Label>
          {draft.taper && (
            <>
              <Checkbox
                id="edit-per-strength-toggle"
                className="ml-4"
                checked={draft.perStrength}
                onCheckedChange={(checked) => togglePerStrength(checked === true)}
              />
              <Label htmlFor="edit-per-strength-toggle" className="text-xs cursor-pointer">
                Allow different strengths per row
              </Label>
            </>
          )}
        </div>

        {draft.taper ? (
          /* Editable taper grid — identical to PrescriptionTable's add flow. */
          <div className="space-y-1.5">
            {draft.perStrength ? (
              <div className={`${TAPER_GRID_DRUG} text-xs text-muted`}>
                <span>Drug</span>
                <span>From date</span>
                <span>Frequency</span>
                <span>Days</span>
                <span>To date</span>
                <span>Instructions</span>
                <span className="text-center">+</span>
                <span className="text-center">×</span>
              </div>
            ) : (
              <div className={`${TAPER_GRID} text-xs text-muted`}>
                <span>From date</span>
                <span>Frequency</span>
                <span>Days</span>
                <span>To date</span>
                <span>Instructions</span>
                <span className="text-center">+</span>
                <span className="text-center">×</span>
              </div>
            )}
            {draft.taper.map((r) => {
              const to = stepToDate(r.fromDate, r.days);
              return (
                <div key={r.key} className={draft.perStrength ? TAPER_GRID_DRUG : TAPER_GRID}>
                  {draft.perStrength && (
                    <TaperRowDrugSearch
                      rowKey={r.key}
                      value={r.name}
                      debounceMs={0}
                      onPick={(d) => setTaperRow(r.key, {
                        drugId: d.id || null,
                        name: d.name,
                        strength: d.strength ?? r.strength,
                        composition: d.composition ?? r.composition,
                        formulation: (d.formulation as MedicineRow["formulation"]) ?? r.formulation,
                      })}
                    />
                  )}
                  <Input
                    type="date"
                    className="h-8"
                    value={r.fromDate}
                    onChange={(e) => setTaperRow(r.key, { fromDate: e.target.value })}
                  />
                  <FrequencyInput
                    className="h-8"
                    value={r.frequency}
                    onChange={(v) => setTaperRow(r.key, { frequency: v })}
                  />
                  <Input
                    type="number"
                    min={1}
                    className="h-8"
                    value={r.days}
                    onChange={(e) => setTaperRow(r.key, { days: e.target.value })}
                  />
                  <div className="flex h-8 items-center gap-1 text-sm text-muted">
                    <CalendarDays size={13} className="shrink-0" />
                    <span className="whitespace-nowrap">{to ? fmtDate(to) : "—"}</span>
                  </div>
                  <Input
                    className="h-8"
                    placeholder="With breakfast"
                    value={r.instructions}
                    onChange={(e) => setTaperRow(r.key, { instructions: e.target.value })}
                  />
                  <Button size="icon" variant="ghost" className="h-8 w-8 text-maroon-600" onClick={addTaperRow} title="Add next row">
                    <Plus size={14} />
                  </Button>
                  <Button
                    size="icon" variant="ghost" className="h-8 w-8 text-red-600"
                    onClick={() => removeTaperRow(r.key)}
                    disabled={draft.taper!.length === 1}
                    title="Remove row"
                  >
                    <X size={14} />
                  </Button>
                </div>
              );
            })}
            <div className="flex items-center gap-1.5 text-xs text-muted">
              <Plus size={12} className="text-maroon-600" />
              {draft.perStrength
                ? <>Use <b>+</b> to append a row. Search a different strength in each row's Drug box; consecutive same-strength rows print under one header.</>
                : <>Use <b>+</b> on a row to append the next period (its From date starts the day after the previous To date).</>}
            </div>
          </div>
        ) : (
          /* Simple drug row — Frequency | Days | Instructions (same as the
             add-drug draft panel, minus the Add button which is in the
             dialog footer here). */
          <div className="grid grid-cols-[180px_110px_1fr] items-end gap-2"
               onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); save(); } }}>
            <div>
              <Label className="text-xs">Frequency</Label>
              <FrequencyInput
                value={draft.frequency}
                onChange={(v) => set("frequency", v)}
                onCommit={save}
              />
            </div>
            <div>
              <Label className="text-xs">Days</Label>
              <Input
                type="number"
                min={1}
                value={draft.days}
                onChange={(e) => set("days", e.target.value)}
                placeholder="5"
              />
            </div>
            <div>
              <Label className="text-xs">Instructions</Label>
              <Input
                value={draft.instructions}
                onChange={(e) => set("instructions", e.target.value)}
                placeholder="e.g. After meals (optional)"
              />
            </div>
          </div>
        )}

        {draft.taper && (
          <div className="flex items-center gap-1.5 text-xs text-muted">
            <Plus size={12} className="text-maroon-600" />
            Days and frequency come from the protocol rows above.
          </div>
        )}

        {/* Footer: Delete on the left, Cancel + Save on the right.
            Escape cancels via Radix Dialog's built-in onOpenChange(false). */}
        <div className="flex justify-between">
          <Button variant="ghost" className="text-red-600" onClick={del}>
            <Trash size={14} className="mr-1" /> Delete medicine
          </Button>
          <div className="flex gap-2">
            <Button variant="ghost" onClick={cancel}>Cancel</Button>
            <Button onClick={save} className="bg-maroon-600 hover:bg-maroon-700 text-cream-50">
              Save
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
